import type { NavigateFunction } from 'react-router-dom';
import type { QueryClient } from '@tanstack/react-query';
import type { SessionResponse } from '@/api/auth.api';
import { setAccessToken } from './session';
import { clearPendingLogin } from './pre-auth';
import { postAuthLanding } from './post-auth-landing';

/**
 * Persist a freshly issued session and land the user. This is the one real
 * point at which an in-flight login is spent - see `clearPendingLogin`'s doc
 * comment for why that matters more than it looks.
 *
 * Shared by `useVerifyTwoFactor` (the normal step-2-completes-it path) and
 * `LoginPage` (the MYN-77-disabled path, where step 1 already is the whole
 * login and there is no step 2 to call this from).
 */
export function completeLoginSession(
  result: SessionResponse,
  opts: { queryClient: QueryClient; navigate: NavigateFunction; returnTo?: string },
): void {
  setAccessToken(result.accessToken);
  opts.queryClient.setQueryData(['auth', 'me'], result.user);
  clearPendingLogin();
  // `replace` so Back cannot return to a login step with spent credentials.
  // An explicit `returnTo` wins; otherwise a stored Issue Report intent resumes
  // the journey (FR-206) and anything else lands on the dashboard as before.
  opts.navigate(opts.returnTo ?? postAuthLanding(), { replace: true });
}
